import React from 'react';
import { FaUsers, FaCircle, FaSearch } from 'react-icons/fa';
import CallHistoryList from './CallHistoryList';

const ConversationList = ({
  loading, filteredConversations, selectedConversation,
  searchQuery, setSearchQuery, activeTab, setActiveTab,
  platformFilter, setPlatformFilter,
  onSelectConversation, onNewChat, formatTime, onlineUsers
}) => (
  <div className="w-full border-r border-gray-200 flex flex-col bg-gray-50" data-testid="conversation-list">
    {/* Search & Tabs */}
    <div className="p-3 space-y-3 border-b border-gray-200">
      {activeTab !== 'calls' && (
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:border-[#4A90E2]"
            data-testid="search-conversations"
          />
        </div>
      )}
      <div className="flex space-x-1">
        {['all', 'direct', 'groups', 'calls'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab}`}
            className={`flex-1 py-1.5 text-xs font-medium rounded-full transition-colors ${
              activeTab === tab
                ? 'bg-[#4A90E2] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
    </div>

    {/* Calls tab */}
    {activeTab === 'calls' ? (
      <CallHistoryList />
    ) : (
      /* Conversations */
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-500" data-testid="conversations-loading">Loading...</div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500" data-testid="no-conversations">
            <p>No conversations yet</p>
            <button onClick={onNewChat} className="mt-2 text-[#4A90E2] text-sm font-medium" data-testid="start-chat-link">
              Start a new chat
            </button>
          </div>
        ) : (
          filteredConversations.map(conv => {
            const isOnline = onlineUsers?.has(conv.id);
            const showOnline = conv.show_online_status !== false;
            return (
              <div
                key={conv.id}
                onClick={() => onSelectConversation(conv)}
                className={`p-3 cursor-pointer transition-colors border-b border-gray-100 ${
                  selectedConversation?.id === conv.id ? 'bg-[#4A90E2]/10' : 'hover:bg-gray-100'
                }`}
                data-testid={`conversation-${conv.id}`}
              >
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    {conv.type === 'group' ? (
                      <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#A855F7] rounded-full flex items-center justify-center">
                        <FaUsers className="text-white text-lg" />
                      </div>
                    ) : (
                      <img
                        src={conv.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'}
                        alt={conv.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    {isOnline && showOnline && (
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1.5">
                        <p className="text-gray-900 font-medium truncate">{conv.name}</p>
                        {isOnline && showOnline && <FaCircle size={6} className="text-green-500 flex-shrink-0" />}
                      </div>
                      {conv.last_message && (
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {formatTime(conv.last_message.created_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm truncate">
                      {conv.last_message?.content || 'No messages yet'}
                    </p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="bg-[#4A90E2] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" data-testid={`unread-badge-${conv.id}`}>
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    )}
  </div>
);

export default ConversationList;
